import React, { useState, useContext, useRef, useEffect } from "react";
import { useNavigate, useSearchParams, Navigate } from "react-router-dom";
import {
  Typography,
  COLORS,
  Table,
  TableContainer,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
} from "pvs-design-system";
import { Box, Stack, Divider } from "@mui/material";
import BootConfigContext from "components/BootConfigContext/BootConfigContext";
import TourModal from "components/virtual-library/TourModal";
import DeleteResourceModal from "components/virtual-library/DeleteResourceModal";
import DetailsPanel from "components/virtual-library/DetailsPanel";
import LibraryErrorBoundary from "components/virtual-library/LibraryErrorBoundary";
import LibraryHeader from "components/virtual-library/LibraryHeader";
import LibrarySearchBar, {
  type SearchMode, type SearchInValue,
} from "components/virtual-library/LibrarySearchBar";
import AdvancedSearchFiltersModal from "components/virtual-library/AdvancedSearchFiltersModal";
import {
  LibraryViewToggle,
  ManageActionsBar,
  LibraryEmptyState,
  LibraryTableSkeleton,
  LibraryGridBarsSkeleton,
  FolderTableRow,
  ResourceTableRow,
  FolderGridCard,
  ResourceGridCard,
  type ViewMode,
} from "components/virtual-library/library-content";
import {
  useGetCategoriesQuery,
  useGetResourcesQuery,
  useDeleteResourceMutation,
} from "api/useLibraryApi";
import { useModalNavigate, useTour } from "hooks";
import { Notify } from "components/Notifications/Notifications";
import { extractErrorMessage } from "utils/errorUtils";
import {
  canManageLibrary,
  canCreateCategory,
  canCreateResource,
  canModifyCategory,
  canDeleteCategory,
  canModifyResource,
  canDeleteResource,
} from "utils/permissionHelper";
import { encodeQueryValue } from 'utils/urlEncoding';
import { ROUTES } from "constants/constants";
import { Category, Resource } from "types";

const ROOT_LINEAGE = "/Home/";

const IndexPage: React.FC = () => {
  const [searchParams] = useSearchParams();
  const incomingLineage = searchParams.get('lineage');

  const bootConfig = useContext(BootConfigContext);
  const { permissions } = bootConfig.authInfo;
  const canManage = canManageLibrary(permissions);
  const isManageModeActive = canManage;
  const navigate = useNavigate();

  const openModal = useModalNavigate();

  const { isTourOpen, tourCompleted, openTour, skipTour, completeTour } =
    useTour();

  const [searchIn, setSearchIn] = useState<SearchInValue>("all");
  const [searchMode, setSearchMode] = useState<SearchMode>("all");
  const [selectedItem, setSelectedItem] = useState<Resource | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<Resource | null>(null);
  const [viewMode, setViewMode] = useState<ViewMode>("list");
  const [settingsOpen, setSettingsOpen] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const { data: categories = [], isLoading, isError: isCategoriesError } = useGetCategoriesQuery(isManageModeActive);
  const { data: rootResourcesData, isError: isResourcesError } = useGetResourcesQuery({
    categoryLineage: ROOT_LINEAGE,
    categoryName: "Home",
    isManageMode: isManageModeActive,
  });
  const rootResources: Resource[] = rootResourcesData?.items ?? [];
  const isBrowseError = isCategoriesError || isResourcesError;

  const [deleteResource] = useDeleteResourceMutation();

  useEffect(() => {
    searchRef.current?.focus();
  }, []);

  const handleSearch = (query: string) => {
    const q = query.trim();
    if (q) {
      const params = new URLSearchParams({ q });
      if (searchIn !== "all") params.set("searchIn", searchIn);
      if (searchMode !== "all") params.set("searchMode", searchMode);
      navigate(`${ROUTES.search}?${params.toString()}`);
    }
  };

  const handleCategoryClick = (cat: Category, e?: React.MouseEvent) => {
    const url = `${ROUTES.browse}?lineage=${encodeQueryValue(cat.lineage ?? `/Home/${cat.name}/`)}`;
    if (e && (e.ctrlKey || e.metaKey)) {
      window.open(url, "_blank", "noopener,noreferrer");
      return;
    }
    navigate(url);
  };

  const handleResourceClick = (resource: Resource) => {
    window.open(resource.url, "_blank", "noopener,noreferrer");
  };

  const handleShowDetails = (resource: Resource, e?: React.MouseEvent) => {
    e?.stopPropagation();
    setSelectedItem(resource);
  };

  const handleDeleteConfirm = async () => {
    if (!deleteTarget) return;
    const { id: deletedId, title } = deleteTarget;
    try {
      await deleteResource(deletedId).unwrap();
      Notify.success(`Resource "${title}" deleted.`);
      // Close the details drawer if it was showing the just-deleted resource,
      // otherwise it lingers with stale content.
      if (selectedItem?.id === deletedId) setSelectedItem(null);
    } catch (e) {
      Notify.error(extractErrorMessage(e, "Failed to delete resource. Please try again."));
    } finally {
      setDeleteTarget(null);
    }
  };

  // Manage handlers. In list view the home page navigates directly to the
  // create/edit/delete route; in grid view it opens the modal over the dimmed
  // page. Both behaviours are preserved exactly as before.
  const handleEditFolderList = (cat: Category) =>
    navigate(ROUTES.editCategory, { state: { editCategory: cat.name, editLineage: ROOT_LINEAGE } });
  const handleDeleteFolderList = (cat: Category) =>
    navigate(ROUTES.deleteCategory, { state: { categoryName: cat.name, lineage: ROOT_LINEAGE } });
  const handleEditFolderGrid = (cat: Category) =>
    openModal(ROUTES.editCategory, { editCategory: cat.name, editLineage: ROOT_LINEAGE });
  const handleDeleteFolderGrid = (cat: Category) =>
    openModal(ROUTES.deleteCategory, { categoryName: cat.name, lineage: ROOT_LINEAGE });
  const handleEditResourceList = (resource: Resource) =>
    navigate(`${ROUTES.editResource}/${resource.id}`);
  const handleEditResourceGrid = (resource: Resource) =>
    openModal(`${ROUTES.editResource}/${resource.id}`);
  const handleDeleteResource = (resource: Resource) => setDeleteTarget(resource);

  const canEditCategories = isManageModeActive && canModifyCategory(permissions);
  const canDeleteCategories = isManageModeActive && canDeleteCategory(permissions);
  const canEditResources = isManageModeActive && canModifyResource(permissions);
  const canDeleteResources = isManageModeActive && canDeleteResource(permissions);

  // When the SPA is opened with ?lineage=... (e.g. from a .NET application link),
  // redirect to SubdirectoryPage for that lineage. Placed after all hooks to
  // satisfy the Rules of Hooks (no conditional hook calls).
  if (incomingLineage) {
    const browseParams = new URLSearchParams({ lineage: incomingLineage });
    return <Navigate to={`${ROUTES.browse}?${browseParams}`} replace />;
  }

  return (
    <>
      <Box
        component="main"
        sx={{
          display: "flex",
          flexDirection: "column",
          background: COLORS.beige[200],
        }}
      >
        {/* Header — utility buttons only (search lives in the hero below) */}
        <LibraryHeader tourCompleted={tourCompleted} onTour={openTour} />

        {/* Main content: scrollable content on the left, details panel on the right */}
        <Box sx={{ display: "flex" }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            {/* Hero section: search + recently accessed */}
            <Box>
              <Box
                sx={{
                  maxWidth: 1100,
                  mx: "auto",
                  mt: 6,
                  px: 4,
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "flex-start",
                }}
              >
                <Typography
                  variant="h2"
                  component="h1"
                  style={{
                    color: COLORS.gray[900],
                    marginBottom: 20,
                    textAlign: "left",
                    fontWeight: 700,
                  }}
                >
                  Virtual Library
                </Typography>

                {/* Hero search — large variant of the shared search bar */}
                <Box id="home-search-bar" sx={{ width: "100%", maxWidth: 740 }}>
                  <LibrarySearchBar
                    onSearch={handleSearch}
                    size="large"
                    placeholder="Search Virtual Library"
                    inputRef={searchRef}
                    settingsOpen={settingsOpen}
                    onSettingsToggle={() => setSettingsOpen((v) => !v)}
                  />
                </Box>
              </Box>
            </Box>

            {/* Browse section — root of the library, formatted like any folder's contents */}
            <Box
              id="browse-section"
              sx={{ maxWidth: 1100, mx: "auto", px: 4, mt: 4, mb: 6 }}
            >
              <LibraryErrorBoundary>
                {/* Manage actions bar + view toggle */}
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  sx={{ mb: 1 }}
                >
                  <ManageActionsBar
                    canAddFolder={isManageModeActive && canCreateCategory(permissions)}
                    canAddResource={isManageModeActive && canCreateResource(permissions)}
                    onAddFolder={() => openModal(ROUTES.createCategory, { lineage: ROOT_LINEAGE })}
                    onAddResource={() => openModal(ROUTES.createResource, { lineage: ROOT_LINEAGE })}
                  />
                  <LibraryViewToggle viewMode={viewMode} onChange={setViewMode} />
                </Stack>

                {viewMode === "grid" ? (
                  isLoading ? (
                    <LibraryGridBarsSkeleton />
                  ) : (
                    <>
                      <Box
                        sx={{
                          display: "grid",
                          gridTemplateColumns:
                            "repeat(auto-fill, minmax(200px, 1fr))",
                          gap: 1.5,
                        }}
                      >
                        {categories.map((cat) => (
                          <FolderGridCard
                            key={cat.id}
                            folder={cat}
                            onOpen={handleCategoryClick}
                            onEdit={handleEditFolderGrid}
                            onDelete={handleDeleteFolderGrid}
                            canEdit={canEditCategories && !!cat.canModify}
                            canDelete={canDeleteCategories && !!cat.canDelete}
                          />
                        ))}
                        {rootResources.map((resource) => (
                          <ResourceGridCard
                            key={resource.id}
                            resource={resource}
                            onOpen={handleResourceClick}
                            onShowDetails={handleShowDetails}
                            onEdit={handleEditResourceGrid}
                            onDelete={handleDeleteResource}
                            canEdit={canEditResources && !!resource.canModify}
                            canDelete={canDeleteResources && !!resource.canDelete}
                            selected={selectedItem?.id === resource.id}
                            showSelection
                          />
                        ))}
                      </Box>

                      {categories.length === 0 &&
                        rootResources.length === 0 && (
                          <LibraryEmptyState
                            isError={isBrowseError}
                            message={isBrowseError ? 'Failed to load library contents. Please try refreshing.' : 'This folder is empty'}
                          />
                        )}
                    </>
                  )
                ) : (
                  <TableContainer
                    sx={{
                      border: `1px solid ${COLORS.gray[200]}`,
                      borderRadius: 2,
                    }}
                  >
                    <Table
                      stickyHeader
                      size="small"
                      aria-label="Virtual Library contents"
                      sx={{
                        tableLayout: "fixed",
                        "& .MuiTableCell-root": {
                          paddingTop: "8px",
                          paddingBottom: "8px",
                        },
                      }}
                    >
                      <TableHead>
                        <TableRow>
                          <TableCell align="center" sx={{ width: 100 }}>
                            Type
                          </TableCell>
                          <TableCell>Name</TableCell>
                          <TableCell sx={{ width: 120 }}>Size</TableCell>
                          <TableCell sx={{ width: 124 }}>Actions</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {isLoading ? (
                          <LibraryTableSkeleton />
                        ) : (
                          <>
                            {/* Folder rows — always listed first */}
                            {categories.map((cat) => (
                              <FolderTableRow
                                key={cat.id}
                                folder={cat}
                                onOpen={handleCategoryClick}
                                onEdit={handleEditFolderList}
                                onDelete={handleDeleteFolderList}
                                canEdit={canEditCategories && !!cat.canModify}
                                canDelete={canDeleteCategories && !!cat.canDelete}
                              />
                            ))}

                            {categories.length > 0 &&
                              rootResources.length > 0 && (
                                <TableRow>
                                  <TableCell
                                    colSpan={4}
                                    sx={{ p: 0, border: "none" }}
                                  >
                                    <Divider
                                      sx={{ borderColor: COLORS.gray[100] }}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}

                            {/* Unassigned resource rows — follow the folders */}
                            {rootResources.map((resource) => (
                              <ResourceTableRow
                                key={resource.id}
                                resource={resource}
                                onOpen={handleResourceClick}
                                onShowDetails={handleShowDetails}
                                onEdit={handleEditResourceList}
                                onDelete={handleDeleteResource}
                                canEdit={canEditResources && !!resource.canModify}
                                canDelete={canDeleteResources && !!resource.canDelete}
                                selected={selectedItem?.id === resource.id}
                                showSelectionBorder
                              />
                            ))}

                            {categories.length === 0 &&
                              rootResources.length === 0 && (
                                <TableRow>
                                  <TableCell
                                    colSpan={4}
                                    sx={{ border: "none" }}
                                  >
                                    <LibraryEmptyState
                                      isError={isBrowseError}
                                      message={isBrowseError ? 'Failed to load library contents. Please try refreshing.' : 'This folder is empty'}
                                    />
                                  </TableCell>
                                </TableRow>
                              )}
                          </>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                )}
              </LibraryErrorBoundary>
            </Box>
          </Box>

          <DetailsPanel
            item={selectedItem}
            onClose={() => setSelectedItem(null)}
            canViewStatistics={canManage}
          />
        </Box>
      </Box>

      <TourModal
        canManage={canManage}
        isOpen={isTourOpen}
        onSkip={skipTour}
        onFinish={completeTour}
      />
      <DeleteResourceModal
        open={deleteTarget !== null}
        resourceTitle={deleteTarget?.title}
        onConfirm={handleDeleteConfirm}
        onCancel={() => setDeleteTarget(null)}
      />
      <AdvancedSearchFiltersModal
        open={settingsOpen}
        onClose={() => setSettingsOpen(false)}
        searchIn={searchIn}
        onSearchInChange={setSearchIn}
        searchMode={searchMode}
        onSearchModeChange={setSearchMode}
      />
    </>
  );
};

export default IndexPage;
